import type { ToolStep } from "./useAgentChat";
import { replayProgressEvents } from "./useConversationProgress";

interface ActivityEvent {
  sequence: number;
  event: string;
  data: Record<string, unknown>;
}

interface ActivityPage {
  activity?:
    | {
        assistant_message_id: string;
        call_id: string;
        events: ActivityEvent[];
      }[]
    | null;
  activity_next_cursor?: Record<string, string> | null;
}

/** Pages may split a message or repeat the latest turn to cover index lag. */
export async function fetchConversationActivity(
  url: string,
  conversationId: string,
  token: string,
): Promise<Map<string, ToolStep[]>> {
  const messages = new Map<string, Map<number, ActivityEvent>>();
  let cursor: Record<string, string> | null = null;
  do {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        conversation_id: conversationId,
        include_activity: true,
        activity_cursor: cursor,
      }),
    });
    if (response.status === 404) return new Map(); // Older backend rollout.
    if (!response.ok) throw new Error("Conversation activity unavailable");
    const page = (await response.json()) as ActivityPage;
    for (const row of page.activity ?? []) {
      const events = messages.get(row.assistant_message_id) ?? new Map();
      for (const event of row.events) events.set(event.sequence, event);
      messages.set(row.assistant_message_id, events);
    }
    cursor = page.activity_next_cursor ?? null;
  } while (cursor);
  return new Map(
    [...messages].map(([messageId, events]) => [
      messageId,
      replayProgressEvents(
        [...events.values()].sort((a, b) => a.sequence - b.sequence),
      ).steps,
    ]),
  );
}
